"use client";

import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { LINK_POSITION_BODY, fireLinkClick } from "../../services/analytics";
import {
  SURFACE_PRESETS,
  isSurfacePreset,
  resolveSurface,
  resolveSurfaceAttribute,
  type SurfacePreset
} from "../../tokens/surface-presets";
import { plainText, richText } from "../text/text";
import styles from "./tab-tray.module.css";
import { withSafeRender } from "../safe-render";

/**
 * TabTray — a segmented control. One choice from a small set, rendered as a
 * pill tray, reporting the selection to its host.
 *
 * ⚠ Not the video-lab TabTray at `src/components/video-lab/tab-tray`, which has
 * its own prop contract and consumers. Import this one by path.
 *
 * A fieldset of radio inputs rather than a tablist: it owns no panels, and
 * `role="tablist"` without `aria-controls` targets is invalid ARIA. No disabled
 * variant and no icon API — neither is designed.
 */

/* Re-exported so `import { SURFACE_PRESETS } from "./tab-tray"` keeps
   resolving; the vocabulary is declared once in `../../tokens/surface-presets`,
   beside the stylesheet that publishes the steps. */
export { SURFACE_PRESETS, isSurfacePreset, resolveSurface, resolveSurfaceAttribute };
export type { SurfacePreset };

/**
 * Who scrolls the tabs when they outgrow the space: the tray itself, or
 * whatever contains it. Exported so a call site and the story controls read the
 * vocabulary instead of restating it.
 */
export const SCROLL_OWNERS = ["internal", "host"] as const;

export type TabTrayScrollOwner = (typeof SCROLL_OWNERS)[number];

/** Whether a NORMALIZED scroll name is one of the two designed modes. */
function isScrollOwner(scroll: string): scroll is TabTrayScrollOwner {
  return (SCROLL_OWNERS as readonly string[]).includes(scroll);
}

/* What was decided about a supplied `scroll`, in the shape `TransitionDecision`
   uses: `rejected` renders exactly what `internal` renders and differs only in
   being worth a diagnostic. */
type ScrollDecision =
  | { kind: "resolved"; owner: TabTrayScrollOwner }
  | { kind: "rejected"; requested: string };

/**
 * Blank normalizes to the DEFAULT rather than to an unknown name — `scroll` is
 * authored, an unset select round-trips as `""` or a lone space, and reporting
 * `unknown scroll ""` for one is the dishonest reading `resolveSurface` exists
 * to avoid. Unknown is a real third answer: it keeps the internal scroller, so
 * a typo cannot silently hand tab reachability to a host that is not scrolling.
 */
function resolveScrollOwner(raw?: string): ScrollDecision {
  if (raw === undefined) {
    return {
      kind: "resolved",
      owner: "internal"
    };
  }

  /* Typed `string`, checked as `unknown` would be, for the same reason
     `resolveTransitionMs` gives: a CMS feed reaches this past the compiler, and
     `.trim()` on a number throws mid-render. */
  if (typeof raw !== "string") {
    return {
      kind: "rejected",
      requested: String(raw)
    };
  }

  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return {
      kind: "resolved",
      owner: "internal"
    };
  }

  return isScrollOwner(trimmed) ?
    {
      kind: "resolved",
      owner: trimmed
    } :
    {
      kind: "rejected",
      requested: trimmed
    };
}

/* The range `transitionMs` is held to. 0 is a legitimate request for "no
   animation" and is not the same as omitting the prop; past a second a tab
   change reads as broken rather than slow. */
const TRANSITION_MS_MIN = 0;
const TRANSITION_MS_MAX = 1000;

/* What was decided about a supplied `transitionMs`. A discriminated union rather
   than independent flags, so `ms` and `clamped` exist only on the arm that has a
   duration; `omitted` and `rejected` both write nothing and differ only in that
   one is worth a diagnostic. */
type TransitionDecision =
  | { kind: "omitted" }
  | { kind: "rejected" }
  | { kind: "resolved"; ms: number; clamped: boolean };

/**
 * Milliseconds, not a CSS duration string: the value is interpolated straight
 * into a `transition`, so a string is an injection point for `var()` and the
 * plausible authoring mistake (`"300"`, no unit) is invalid CSS that fails
 * silently. Nonsense is rejected and out-of-range is clamped; both say so in
 * development.
 */
function resolveTransitionMs(raw?: number): TransitionDecision {
  if (raw === undefined) {
    return { kind: "omitted" };
  }

  /* Typed `number`, checked as `unknown` would be: a CMS feed reaches this past
     the compiler, and a `transition` carrying `"300"` is dropped whole. */
  if (typeof raw !== "number" || !Number.isFinite(raw)) {
    return { kind: "rejected" };
  }

  const ms = Math.min(Math.max(raw, TRANSITION_MS_MIN), TRANSITION_MS_MAX);
  return {
    kind: "resolved",
    ms,
    clamped: ms !== raw
  };
}

export interface TabTrayItem {
  /** Stable value reported through onChange and submitted with the form. */
  value: string;
  /** Visible tab text. Single line; the tab hugs it — unless `fill` is set, which
   *  stretches the tabs to share the full-width tray below 735px. */
  label: string;
}

/** The tray's height treatment: `medium` (56px tray / 48px tabs) or `small`
 *  (44px tray / 36px tabs). Exported so a host types its own size prop off this
 *  rather than copying the union. */
export type TabTraySize = "medium" | "small";

interface TabTraySharedProps {
  /**
   * Tabs in display order. An entry needs a non-empty `value` and a non-blank
   * `label` or it is dropped; a repeated `value` is dropped too.
   */
  items?: TabTrayItem[];
  /**
   * Accessible name for the group, rendered as a visually hidden legend. It is
   * trimmed, and a blank one is reported in development.
   */
  ariaLabel: string;
  /**
   * Where the tray sits on the page, for the `link_click` each settled
   * activation reports. Defaults to the body position.
   */
  linkPosition?: string;
  /** medium = 56px tray / 48px tabs; small = 44px tray / 36px tabs. */
  size?: TabTraySize;
  /**
   * Which published surface the tray is painted on — one of `SURFACE_PRESETS`.
   * Omit it and the tray keeps its own designed background, which is NOT one of
   * the presets, so omitted is a third state rather than a synonym for one of
   * the six. An unrecognised value renders the default tray and is reported in
   * development.
   */
  surface?: SurfacePreset | (string & {});
  /**
   * How long a tab change takes to animate, in milliseconds. Omit it and the
   * tray uses `--duration-fast` (150ms). It drives the label cross-fade and the
   * anchor-positioned pill together, so the two cannot be configured out of
   * step. Accepted range is 0–1000ms; outside it is clamped, non-finite is
   * ignored, both reported in development. It moves the animation and nothing
   * else — not when analytics fires (see ACTIVATION_SETTLE_MS).
   */
  transitionMs?: number;
  /**
   * Who scrolls the tabs. `internal`, the default, keeps the tray's own
   * scroller: the tray clamps to its container and the tabs scroll inside its
   * edge. `host` renders the tray at its full intrinsic width with no scroller
   * and no clamp, for a container that clips and scrolls it — and hands that
   * container tab reachability with it, including bringing the selected tab
   * into view. Blank counts as omitted; an unrecognised value keeps the
   * internal scroller and is reported in development.
   */
  scroll?: TabTrayScrollOwner | (string & {});
  /**
   * Stretch the tabs to fill the tray when it is wider than they are — which is
   * only below 735px, where the tray spans its container (above it the tray hugs
   * its tabs, so there is nothing to fill). Off by default: the tabs sit at the
   * start and the tray's own fill shows to their right. The stretch is
   * proportional, not equal segments. A per-consumer choice at a width Figma does
   * not settle — see design-conflicts/tab-tray.mdx.
   */
  fill?: boolean;
  /**
   * Native radio-group name, auto-generated when omitted. HTML scopes radio
   * exclusivity by name, so two trays given the same name share one selection.
   */
  name?: string;
  /** Extra class for the tray element. Applied last, so it wins the cascade. */
  className?: string;
}

/* `?: never` rather than omission is what makes the halves exclusive — an
   omitted key is still admitted by excess-property checking. The runtime
   diagnostics stay because this repo builds with `typescript.ignoreBuildErrors`. */
export type TabTrayProps = TabTraySharedProps &
  (
    | {
        /** Selected value. The host owns the selection and must accept changes. */
        value: string;
        /** Reports the newly selected item's value. Required alongside `value`. */
        onChange: (value: string) => void;
        /** Meaningless when controlled — the host's `value` decides. */
        defaultValue?: never;
      }
    | {
        /** Omit to let the tray own its selection. */
        value?: never;
        /** Starting selection for the uncontrolled form. Defaults to the first item. */
        defaultValue?: string;
        /** Reports the newly selected item's value. */
        onChange?: (value: string) => void;
      }
  );

/**
 * How long the tray waits for the selection to settle before reporting it.
 * Arrow keys move AND select on a native radio group, so a run of steps commits
 * several selections for one choice; each cancels the pending report.
 *
 * ⚠ A CONSTANT, deliberately not `transitionMs`: the window is bounded by human
 * input — longer than a held-key repeat, shorter than two deliberate choices —
 * and a tray configured at 1000ms would merge two choices into one
 * `link_click`. It equals `--duration-fast`, and the spec pins that as a
 * floor against tokens/motion.css.
 */
const ACTIVATION_SETTLE_MS = 150;

/**
 * The Grow Card Carousel rail's own 5px
 * (`organisms/grow-card-carousel/rail-scroll.client.ts`), deliberately NOT imported —
 * an atom reaching into an organism inverts the dependency. The spec pins the two.
 */
const DRAG_THRESHOLD_PX = 5;

/**
 * The page the reader is on, absolute and lower case — what a tab choice
 * reports as its destination, because the choice leaves them where they are.
 *
 * ⚠ ORIGIN AND PATH ONLY, the shape `services/page-url.ts`'s `currentPageUrl` uses.
 * The query string and the hash vary between two readers who made the same
 * choice on the same page — a `?gtm=no` or an `#anchor` — so carrying them
 * splits one tab choice across GTM rows that are the same click.
 *
 * `undefined` rather than "" when there is no window: `fireLinkClick` spreads
 * the destination conditionally, so a falsy one leaves the key ABSENT, and GTM
 * reports an empty string as a real value.
 */
function currentPageDestination(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const { origin, pathname } = window.location;
  return `${origin}${pathname}`.toLowerCase();
}

/* Checked as `unknown` would be: a CMS feed reaches this past the compiler, and
   a numeric or whitespace-only label passes a bare truthiness test. */
function isRenderable(item?: TabTrayItem): boolean {
  return (
    typeof item?.value === "string" &&
    item.value.length > 0 &&
    typeof item.label === "string" &&
    item.label.trim().length > 0
  );
}

function selectRenderableItems(items?: TabTrayItem[]) {
  const renderable: TabTrayItem[] = [];
  const duplicates: string[] = [];
  const seen = new Set<string>();
  let malformed = 0;

  for (const item of items ?? []) {
    if (!isRenderable(item)) {
      malformed += 1;
      continue;
    }

    if (seen.has(item.value)) {
      duplicates.push(item.value);
      continue;
    }

    seen.add(item.value);
    renderable.push(item);
  }

  return {
    renderable,
    malformed,
    duplicates
  };
}

/* Development-only, and called BEFORE the empty-tray early return: sited after
   it, the one case where the reader sees nothing could never be reported. One
   object rather than eleven positional parameters, so a transposed call site is
   a type error. */
type DevelopmentWarningInput = {
  items: TabTrayItem[];
  malformed: number;
  duplicates: string[];
  latchedControlled: boolean;
  hasOnChange: boolean;
  ariaLabel: string;
  size: string;
  /** ALREADY NORMALIZED by `resolveSurface` — see the check that reads it. */
  surface?: string;
  /** The decision `resolveTransitionMs` reached, and the raw prop it saw. */
  transition: TransitionDecision;
  transitionMs?: number;
  /**
   * The decision `resolveScrollOwner` reached. Its rejected arm names the value
   * it dropped, so there is no raw prop to pass alongside it.
   */
  scroll: ScrollDecision;
  value?: string;
  defaultValue?: string;
  /**
   * Per-instance latch for the `scroll="host"` notice below: that branch
   * reports a correct configuration rather than a mistake, so it never
   * self-clears the way every other branch here does once the authoring
   * error is fixed. A ref rather than module state — two trays on one page
   * are two instances, and each earns its own warning.
   */
  hostScrollWarnedRef: React.MutableRefObject<boolean>;
};

function warnInDevelopment({
  items,
  malformed,
  duplicates,
  latchedControlled,
  hasOnChange,
  ariaLabel,
  size,
  surface,
  transition,
  transitionMs,
  scroll,
  value,
  defaultValue,
  hostScrollWarnedRef
}: DevelopmentWarningInput) {
  const valueGiven = value !== undefined;

  if (ariaLabel.trim().length === 0) {
    console.error(
      "[TabTray] `ariaLabel` is blank, so the group has no accessible name and " +
        "a screen reader announces the tabs without ever saying what is being " +
        "chosen. Pass the wording that names the choice — \"Package type\", for " +
        "example. Each tab names itself; the group has no visible text to borrow."
    );
  }

  if (size !== "medium" && size !== "small") {
    console.warn(
      `[TabTray] unknown size "${size}", so the tray renders at medium. ` +
        "The designed sizes are `medium` and `small`."
    );
  }

  /* An unknown preset renders a perfectly good tray, which is the problem:
     `resolveSurfaceAttribute` drops it, both `var()` fallbacks apply, and what
     paints is the default tray rather than the one asked for. The value read
     here is the NORMALIZED one, so a CMS select round-tripping as `""` reports
     nothing. */
  if (surface !== undefined && !isSurfacePreset(surface)) {
    console.warn(
      `[TabTray] unknown surface "${surface}", so no preset applies and the ` +
        "tray paints its own designed background. The published presets are " +
        `${SURFACE_PRESETS.join(", ")}.`
    );
  }

  /* Written into a `transition`, a unitless or non-numeric value invalidates
     the whole declaration, so the cross-fade would vanish rather than mis-time.
     Rejected before it gets there; this says so. */
  if (transition.kind === "rejected") {
    console.warn(
      `[TabTray] ignored transitionMs ${JSON.stringify(transitionMs)} — it is ` +
        "not a finite number, so the tray keeps the system duration " +
        "(--duration-fast, 150ms). Pass a plain count of milliseconds, e.g. 300."
    );
  } else if (transition.kind === "resolved" && transition.clamped) {
    console.warn(
      `[TabTray] clamped transitionMs ${JSON.stringify(transitionMs)} to ` +
        `${transition.ms}ms. The accepted range is ${TRANSITION_MS_MIN}–` +
        `${TRANSITION_MS_MAX}ms: past a second a tab change reads as broken ` +
        "rather than slow, and a negative duration is not a duration."
    );
  }

  if (scroll.kind === "rejected") {
    console.warn(
      `[TabTray] unknown scroll "${scroll.requested}", so the tray keeps its ` +
        `own scroller. The two modes are ${SCROLL_OWNERS.join(", ")}.`
    );
  } else if (scroll.owner === "host" && !hostScrollWarnedRef.current) {
    /* NOT a mistake being reported — a correctly configured tray, warning about
       the half of the contract it cannot fulfil itself. The tray has no
       scroller in this mode, so it no longer corrects the scroll position on a
       selection change, and nothing here can check that the host does.
       Latched to fire once per instance: every other branch here reports a
       mistake and stops once it is fixed, but this one describes a standing,
       correct configuration and would otherwise repeat on every re-render. */
    hostScrollWarnedRef.current = true;
    console.warn(
      "[TabTray] scroll=\"host\": the tray renders at its full width and " +
        "scrolls nothing, so IT NO LONGER BRINGS THE SELECTED TAB INTO VIEW. " +
        "Whatever scrolls the tray owns that now, on every selection change " +
        "however it was made — the arrow keys move the selection with focus, " +
        "so a selection walking past the edge otherwise leaves focus somewhere " +
        "invisible, which is a WCAG 2.4.11 focus-appearance failure rather " +
        "than a cosmetic one. The tabs past the edge also have to stay " +
        "reachable by drag, swipe and wheel. Use the default internal " +
        "scroller unless the host does both."
    );
  }

  if (malformed > 0) {
    console.warn(
      `[TabTray] dropped ${malformed} malformed item${malformed === 1 ? "" : "s"}. ` +
        "Every tab needs a non-empty `value` and a non-blank `label`: the value " +
        "is the native radio identity and the React key, and a tab with no " +
        "label is a focusable radio with no accessible name and no reportable " +
        "event name."
    );
  }

  for (const duplicate of duplicates) {
    console.warn(
      `[TabTray] dropped a duplicate item value "${duplicate}". A value is both ` +
        "the native radio identity and the React key, so duplicates would " +
        "toggle together and defeat exactly-one selection."
    );
  }

  if (items.length === 0) {
    console.warn(
      "[TabTray] no renderable items, so the tray renders nothing at all. " +
        "Check the authored list reached the component and that its entries " +
        "carry both `value` and `label`."
    );
    return;
  }

  /* Stands in for React's own "checked without onChange" warning, which no
     longer fires because the inputs always carry a handler. */
  if (valueGiven && !hasOnChange) {
    console.error(
      "[TabTray] `value` is set but no `onChange` was given, so the selection " +
        "can never change — the tray is frozen at the current value. Pass " +
        "`onChange` to own the selection, or use `defaultValue` and let the " +
        "tray own it."
    );
  }

  if (!latchedControlled && valueGiven) {
    console.error(
      "[TabTray] `value` arrived after mount, having been undefined at the " +
        "first render. Controlled mode is latched then, so the tray keeps " +
        "owning the selection and `value` is ignored — pass `value` from the " +
        "start (render the tray once an async initial selection is known), or " +
        "stay uncontrolled and use `defaultValue`."
    );
  } else if (latchedControlled && !valueGiven) {
    console.error(
      "[TabTray] `value` went undefined after mount, having been set at the " +
        "first render. Rather than sit with nothing selected, the tray has " +
        "taken the selection back and now owns it — keep passing `value` for " +
        "as long as the tray is mounted, or start uncontrolled with " +
        "`defaultValue`."
    );
  } else if (valueGiven && !items.some(item => item.value === value)) {
    console.error(
      `[TabTray] controlled value "${value}" matches no item, so no tab is ` +
        "selected and the tray looks broken. Keep `value` in step with `items`."
    );
  }

  if (latchedControlled && valueGiven && defaultValue !== undefined) {
    console.warn(
      "[TabTray] `defaultValue` is ignored while the tray is controlled — " +
        "`value` owns the selection. Drop `defaultValue`, or drop `value` and " +
        "let the tray own it."
    );
  }
}

function TabTrayImpl({
  items,
  ariaLabel,
  value,
  defaultValue,
  onChange,
  linkPosition,
  size = "medium",
  surface,
  transitionMs,
  scroll,
  fill,
  name,
  className
}: TabTrayProps) {
  const { renderable: safeItems, malformed, duplicates } = selectRenderableItems(items);
  const transition = resolveTransitionMs(transitionMs);
  const scrollOwner = resolveScrollOwner(scroll);
  /* The MODE, not the request: an unrecognised value renders the internal
     scroller, so the render and the effect read this and only the diagnostic
     reads the decision. */
  const hostScrolled = scrollOwner.kind === "resolved" && scrollOwner.owner === "host";
  /* Extracted because a comparison in the effect's dependency array below is a
     "complex expression" and fails lint. It stands for the scroller's EXISTENCE:
     an empty tray renders `null`, so there is no element to bind to. */
  const hasItems = safeItems.length > 0;
  /* Computed ONCE and fed to the diagnostic. The JSX renders the GATED value
     below instead — the warning must name the unknown value the gate drops. */
  const resolvedSurface = resolveSurface(surface);
  /* Gated on the published presets, not merely normalized: an unknown non-blank
     name rendered as a present attribute dodges surface.css's absence-keyed
     reset and inherits a preset-carrying ancestor's fill and blur. */
  const surfaceAttribute = resolveSurfaceAttribute(surface);

  const autoName = useId();
  /* Latched at mount: a `value` arriving late would otherwise flip the inputs
     from uncontrolled to controlled and reset what the reader chose. */
  const [latchedControlled] = useState(() => value !== undefined);
  /* Not symmetric: a latched tray whose `value` turns undefined has nothing to
     render and no way to move, so the tray takes the selection back. */
  const controlledNow = latchedControlled && value !== undefined;
  /* State rather than the DOM, so the reconcile below can repair it on every
     render. */
  const [chosenValue, setChosenValue] = useState<string | undefined>(undefined);

  const startingValue = safeItems.some(item => item.value === defaultValue) ?
    defaultValue :
    safeItems[0]?.value;

  /* The per-render reconcile: a chosen value that no longer names an item falls
     back to the starting selection. Derived, so there is no second render. */
  const chosenIsReal = safeItems.some(item => item.value === chosenValue);
  const selected = controlledNow ? value : chosenIsReal ? chosenValue : startingValue;

  /* Refs rather than dependencies, and declared first so both are current on
     the same commit: `safeItems` is a new array on every render. */
  const itemsRef = useRef(safeItems);
  const positionRef = useRef(linkPosition);
  useEffect(() => {
    itemsRef.current = safeItems;
    positionRef.current = linkPosition;
  });

  /* Written by `handleChange` alone — that is, by a native `change` event — so
     no derived movement of `selected` can be mistaken for a choice. */
  const activatedRef = useRef<string | null>(null);
  const spendRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /* Seeded with the mount-time selection, so a reader who lands back on the
     value already reported does not report it twice. */
  const reportedRef = useRef<string | undefined>(selected);
  const pendingRef = useRef<string | null>(null);

  /* The guard below catches only a controlled `value` naming no item —
     unlabelled tabs are dropped before they render. */
  const reportSettledSelection = useCallback((settled: string) => {
    pendingRef.current = null;
    reportedRef.current = settled;

    const label = itemsRef.current.find(item => item.value === settled)?.label;
    if (!label) {
      return;
    }

    fireLinkClick({
      /* FOLDED AND TRIMMED HERE, not in the service: `fireLinkClick` pushes
         `linkName` as given on purpose, so folding it there would move every
         other caller's reported wording at once. Authored labels arrive cased
         for the screen and a CMS feed round-trips them padded, and GTM files
         "MySports" and " mysports " as rows of their own.
         Read through `plainText` first, the same reduction an accessible name
         takes, so a label authored as `<sup>®</sup>` is filed under the wording
         the reader sees rather than pushing its raw tags into GTM. The trim
         stays on top of it: a no-break space survives that reduction. */
      linkName: plainText(label).trim().toLowerCase(),
      linkPosition: positionRef.current || LINK_POSITION_BODY,
      /* The page the reader is on. A tab navigates nowhere, so there is no
         destination to read off an href — but the dimension still has to be
         filled, or a tab choice is the one `link_click` row with no page on it
         (WAT-12596). */
      linkDestination: currentPageDestination()
    });
  }, []);

  useEffect(() => {
    /* `selected` must be what the reader activated, not merely different from
       last time, or every derived movement reports a click nobody made. */
    const isReaderActivation = selected !== undefined && selected === activatedRef.current;

    if (!isReaderActivation || selected === reportedRef.current) {
      /* Clear both. A pending value left set lets the flush report a tab the
         reader has already moved off; an activation left set stays armed, so a
         programmatic move to that value later reports a click nobody made. */
      pendingRef.current = null;
      activatedRef.current = null;
      return undefined;
    }

    pendingRef.current = selected;
    const timer = setTimeout(() => reportSettledSelection(selected), ACTIVATION_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [reportSettledSelection, selected]);

  /* The flush. React runs no cleanup on a real document teardown, and
     `services/analytics` arms its own net only for payloads already handed
     over — so a selection still inside the settle window needs these two events
     to reach the queue. `visibilitychange` is the one mobile Safari fires. */
  useEffect(() => {
    const flushPending = () => {
      if (pendingRef.current !== null) {
        reportSettledSelection(pendingRef.current);
      }
    };

    const flushIfHidden = () => {
      if (document.visibilityState === "hidden") {
        flushPending();
      }
    };

    window.addEventListener("pagehide", flushPending);
    document.addEventListener("visibilitychange", flushIfHidden);

    return () => {
      window.removeEventListener("pagehide", flushPending);
      document.removeEventListener("visibilitychange", flushIfHidden);
      flushPending();

      /* Cleared together: adding a dependency here turns the cleanup into a
         mid-life re-run where a stale `activatedRef` gates the next report. */
      if (spendRef.current !== null) {
        clearTimeout(spendRef.current);
        spendRef.current = null;
      }
      activatedRef.current = null;
    };
  }, [reportSettledSelection]);

  /* Keep the selected tab in view: the tray is one row with `overflow-x: auto`,
   * so arrow keys can walk the selection into a clipped tab (a WCAG 2.4.11
   * focus-appearance failure) and a `defaultValue` beyond the strip can mount
   * looking unselected.
   *
   * ⚠ `scroll="host"` has no scroller here to correct, so the effect bails and
   * the HOST owns the correction — see the warning warnInDevelopment raises for
   * that mode.
   *
   * `scrollLeft` on the viewport rather than `scrollIntoView`, which walks the
   * whole ancestor chain and would scroll the page to a tray below the fold.
   * Measured on the LABEL, not the input — `.small`'s input is 8px taller than
   * its tab — and summed up the `offsetParent` chain, because a label's offset
   * parent is `.slot` and read directly every label reports 0.
   *
   * `useEffect` and not `useLayoutEffect`, which warns on the server render. LTR
   * only, and verified in jsdom only: changing anything here means opening a
   * scrolled tray in a browser. */
  /* Latches the `scroll="host"` development warning to once per instance —
     see `hostScrollWarnedRef` on `DevelopmentWarningInput`. */
  const hostScrollWarnedRef = useRef(false);
  const viewportRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    if (hostScrolled) {
      return;
    }

    const viewport = viewportRef.current;
    const label = viewport?.querySelector<HTMLElement>("input:checked + label");
    if (!viewport || !label) {
      return;
    }

    let start = 0;
    for (
      let node: HTMLElement | null = label;
      node && node !== viewport && viewport.contains(node);
      node = node.offsetParent as HTMLElement | null
    ) {
      start += node.offsetLeft;
    }
    const end = start + label.offsetWidth;

    const band = parseFloat(getComputedStyle(viewport).paddingInlineStart) || 0;
    const scrolled = viewport.scrollLeft;

    let target = scrolled;
    if (start < scrolled + band) {
      target = start - band;
    } else if (end > scrolled + viewport.clientWidth - band) {
      target = end - viewport.clientWidth + band;
    }

    target = Math.max(0, target);
    if (target !== scrolled) {
      viewport.scrollLeft = target;
    }
  }, [hostScrolled, selected]);

  /* Drag-to-scroll on NATIVE listeners rather than React handler props: the
     gesture's trailing click has to be caught in the native capture phase, and
     splitting the halves across the two attachment models gates them apart. */
  useEffect(() => {
    /* `scroll="host"` gives `.viewport` `overflow: visible`, so there is no
       scroll position to move — and the host that clips the tray runs its own
       drag on the element wrapping this one. */
    if (hostScrolled) {
      return undefined;
    }

    const viewport = viewportRef.current;
    if (!viewport) {
      return undefined;
    }

    /* Closed over rather than held in refs: the gesture is born and dies inside
       this effect, and nothing renders from it. */
    let press: {
      x: number;
      scrollLeft: number;
      dragging: boolean;
      pointerId: number;
    } | null = null;
    let dragged = false;

    const onPointerDown = (event: PointerEvent) => {
      /* The same pointer coming back spends the previous gesture's suppression;
         a DIFFERENT one arriving while a drag is live must not, or that drag's
         trailing click is free to select a tab and report it. */
      if (!press || event.pointerId === press.pointerId) {
        dragged = false;
      }

      if (event.pointerType === "touch") {
        return;
      }

      /* A right-click can open a gesture whose `pointerup` is delivered to the
         context menu instead, leaving a tray that scrolls on the next hover. */
      if (event.button !== 0) {
        return;
      }

      /* Nothing to scroll, nothing to arm: a tray whose tabs all fit still
         crosses the threshold on a wobble, and the trailing click is then
         suppressed. Last, because `scrollWidth` forces layout. */
      if (viewport.scrollWidth <= viewport.clientWidth) {
        return;
      }

      press = {
        x: event.clientX,
        scrollLeft: viewport.scrollLeft,
        dragging: false,
        pointerId: event.pointerId
      };

      /* Set from the PRESS, not the threshold: Chrome starts a native text drag
         from a mousedown on a label, and that eats the moves that would cross
         it — so `user-select` has to be off from here. */
      viewport.dataset.pressing = "true";
    };

    /* On the WINDOW, not the scroller. Capture is only taken at the threshold,
       so a press starting on the tray's rim and pulling OUTWARD puts its first
       move outside the box, where a viewport-bound listener never sees it. */
    const onPointerMove = (event: PointerEvent) => {
      if (!press) {
        return;
      }

      /* Another pointer's move is not this drag's: measured from an anchor it
         never touched the strip jumps, and the capture below would be taken
         with ITS id, retargeting that pointer's events — click included. */
      if (event.pointerId !== press.pointerId) {
        return;
      }

      /* Nothing is held, so this press's `pointerup` never arrived. Left armed,
         an ordinary hover anywhere on the page drags the strip. Ended rather
         than ignored, so the flags and the pointer capture go too. */
      if (event.buttons === 0) {
        release(event);
        return;
      }

      const travelled = event.clientX - press.x;
      if (!press.dragging) {
        if (Math.abs(travelled) < DRAG_THRESHOLD_PX) {
          return;
        }

        press.dragging = true;
        dragged = true;
        /* At the THRESHOLD, never at the press: capture retargets the trailing
           click to this element, where `suppressClick` is bound — taken at
           `pointerdown` it pulls a plain sub-threshold click off the radio. */
        viewport.setPointerCapture(event.pointerId);
        viewport.dataset.dragging = "true";
      }

      viewport.scrollLeft = press.scrollLeft - travelled;
    };

    /* On the window: a press that lifts outside otherwise stays armed, and the
       next hover reads as a drag. */
    const release = (event: PointerEvent) => {
      /* A stranger's release must not end this gesture. Clear a press still
         inside the threshold and every later move of the real pointer bounces
         off `!press`, so the drag's trailing click would change the selection. */
      if (press && event.pointerId !== press.pointerId) {
        return;
      }

      const finished = press;
      press = null;
      delete viewport.dataset.pressing;

      if (!finished) {
        return;
      }

      delete viewport.dataset.dragging;
      /* By the PRESS's id, not the event's: capture was taken with that id. */
      if (finished.dragging && viewport.hasPointerCapture(finished.pointerId)) {
        viewport.releasePointerCapture(finished.pointerId);
      }

      /* A drag leaves focus on the radio under the press, so the next arrow key
         counts from the pressed tab, not the selection. `preventScroll`, or
         focusing the checked tab scrolls it into view and undoes the drag. */
      if (finished.dragging) {
        const checked = viewport.querySelector<HTMLInputElement>("input:checked");
        const active = document.activeElement;
        if (checked && active !== checked && viewport.contains(active)) {
          checked.focus({ preventScroll: true });
        }
      }

      /* `pointercancel` fires no compatibility click, and the nothing-held path
         is reached only after the real `pointerup` was lost — so neither ending
         leaves a trailing click to spend the flag. */
      if (event.type !== "pointerup") {
        dragged = false;
      }
    };

    /* The drag's trailing click, suppressed rather than ignored. `inset`
       positions `.input` over the tab and `inline-size`/`block-size` size it
       explicitly to at least the same box — `.small` extends it; see
       `.small .input` — so that click lands on a radio, which would select
       itself and report a choice the reader only dragged past. */
    const suppressClick = (event: MouseEvent) => {
      /* `detail: 0` is a native radio group's own click for an arrow key, and
         the shape a screen reader's virtual activation takes. A released drag
         holds the flag until a pointer click spends it, and none need come. */
      if (!dragged || event.detail === 0) {
        return;
      }

      event.preventDefault();
      /* `stopPropagation` is the half that carries this: React attaches at the
         root and derives a radio's `change` from the native CLICK, so a click
         that reaches the root has already run `handleChange`. */
      event.stopPropagation();
      /* Spent inside the branch that suppressed. Cleared above the guard
         instead, an arrow key's click would consume it without suppressing. */
      dragged = false;
    };

    viewport.addEventListener("pointerdown", onPointerDown);
    /* NATIVE and CAPTURING so it runs before the click reaches the input.
       `onClickCapture` would not: React replays its own capture phase from the
       root, by which time the native capture phase has already passed here. */
    viewport.addEventListener("click", suppressClick, true);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);

    return () => {
      viewport.removeEventListener("pointerdown", onPointerDown);
      viewport.removeEventListener("click", suppressClick, true);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", release);
      delete viewport.dataset.pressing;
      delete viewport.dataset.dragging;

      /* A gesture can be live here and the listeners that would release the
         capture leave with the effect — `scroll` flipping to "host" mid-drag
         keeps this element in the document still holding it. */
      if (press && viewport.hasPointerCapture(press.pointerId)) {
        viewport.releasePointerCapture(press.pointerId);
      }
    };

    /* ⚠ `hasItems` IS LOAD-BEARING. An empty tray renders `null`, so this
       effect bails with no scroller — and the component stays MOUNTED across
       the change, so nothing else re-runs it. */
  }, [hasItems, hostScrolled]);

  if (process.env.NODE_ENV !== "production") {
    warnInDevelopment({
      items: safeItems,
      malformed,
      duplicates,
      latchedControlled,
      hasOnChange: Boolean(onChange),
      ariaLabel,
      size,
      surface: resolvedSurface,
      transition,
      transitionMs,
      scroll: scrollOwner,
      value,
      defaultValue,
      hostScrollWarnedRef
    });
  }

  if (safeItems.length === 0) {
    return null;
  }

  /* Element ids come from useId, not `name`: two trays sharing an explicit
     `name` would emit identical ids and `htmlFor` resolves to the first match. */
  const groupName = name || autoName;

  const classes = [
    styles.tray,
    size === "small" ? styles.small : styles.medium,
    hostScrolled ? styles.hostScrolled : "",
    fill ? styles.fill : "",
    className
  ]
    .filter(Boolean)
    .join(" ");

  /* The one inline style, and only when asked for. `transitionMs` reaches CSS as
     a custom property rather than a `transition` shorthand, so the stylesheet
     keeps which properties animate and with what easing. Left undefined when
     there is nothing to say, so an unconfigured tray renders no `style`. */
  const trayStyle =
    transition.kind === "resolved" ?
      ({ "--tab-transition-duration": `${transition.ms}ms` } as React.CSSProperties) :
      undefined;

  /* Reporting is not here — it is driven off the settled selection by the
     effect above. */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const activated = event.target.value;

    /* Set before the state update so the reporting effect sees it on the same
       commit. */
    activatedRef.current = activated;

    /* Then expire it after one window: a host that declines the activation
       re-renders nothing, so the reporting effect never clears the ref. */
    if (spendRef.current !== null) {
      clearTimeout(spendRef.current);
    }
    spendRef.current = setTimeout(() => {
      activatedRef.current = null;
      spendRef.current = null;
    }, ACTIVATION_SETTLE_MS);

    if (!controlledNow) {
      setChosenValue(activated);
    }

    onChange?.(activated);
  };

  return (
    /* `data-dds-surface` is absent unless `surface` names a published preset,
       and blank and unknown both count as absent: React renders `""` as a
       PRESENT attribute, which drops the tray out of surface.css's
       `:not([data-dds-surface])` reset while matching no preset selector — so
       inside a `glass-surface` panel the tray would take the panel's fill. */
    <fieldset className={classes} data-dds-surface={surfaceAttribute} style={trayStyle}>
      {/* Whitespace is not an accessible name, and untrimmed it looks present
          to anything checking only for a non-empty string. */}
      <legend className="dds-sr-only">{ariaLabel.trim()}</legend>
      {/* The scroller is an element of its own because the rim and the pill are
          absolutely positioned against the tray, and such a descendant of a
          scroll container scrolls with its content — so `overflow-x` on the
          fieldset would scroll the tray's own edge away from its tabs. */}
      <span className={styles.viewport} ref={viewportRef}>
        {safeItems.map((item, index) => {
          const id = `${autoName}-opt${index}`;
          return (
            <span key={item.value} className={styles.slot}>
              <input
                checked={selected === item.value}
                className={styles.input}
                id={id}
                name={groupName}
                /* Always attached, which satisfies React's "checked without
                   onChange" check — warnInDevelopment raises that case instead. */
                onChange={handleChange}
                type="radio"
                value={item.value}
              />
              <label className={styles.label} htmlFor={id}>
                {/* Drawn through the parse Text uses, so an authored
                    `<sup>®</sup>` is the element and not its own tags on
                    screen. The span is unchanged: a label with no markup is
                    handed back the string it came in as. */}
                <span className={styles.text}>{richText(item.label)}</span>
              </label>
            </span>
          );
        })}
        {/* The sliding active pill — decorative, and inert until the
            stylesheet's anchor-positioning block takes over. Inside the
            scroller so it travels with the tabs and is clipped with them. */}
        <span aria-hidden="true" className={styles.pill} />
      </span>
    </fieldset>
  );
}
export const TabTray = withSafeRender(TabTrayImpl, "TabTray");

export default TabTray;
